import CheckIcon from "@mui/icons-material/CheckRounded";
import EditIcon from "@mui/icons-material/EditOutlined";
import UndoIcon from "@mui/icons-material/UndoRounded";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import { useProjectStore } from "@stores/projectStore";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  usePatchRequirement,
  useRequirementsSpec,
} from "../hooks/useRequirements";
import type {
  CategoryKey,
  Priority,
  Requirement,
  RequirementStatus,
  VerificationMethod,
} from "../types";
import { RequirementId } from "./atoms";
import { patchRejectionKey, useDecisionStore } from "./decisionStore";

/* ── Tool-call shapes the backend emits ──────────────────────────────────
 *
 * `propose_requirement` returns:
 *   { kind: "propose", handle, preview: { category, statement, ... } }
 *
 * `update_requirement` returns:
 *   { kind: "proposeUpdate", requirementId, field, before, after }
 *
 * Both surface inline as patch cards; user accepts before any DB write
 * happens.
 */

interface ProposeResult {
  kind: "propose";
  handle: string;
  /** Public ID of the just-committed suggestion. Present whenever the
   *  backend wrote the row at status='suggested' (i.e. always, post the
   *  list-side review pipeline). The chat-side render uses this to
   *  link directly to the row. */
  requirementId?: string;
  preview: {
    category: CategoryKey;
    statement: string;
    priority: Priority;
    status: RequirementStatus;
    rationale: string | null;
    acceptance: string | null;
    verification: VerificationMethod | null;
    source: string;
  };
}

interface ProposeUpdateResult {
  kind: "proposeUpdate";
  requirementId: string;
  field: string;
  before: unknown;
  after: unknown;
}

interface PhaseChangeResult {
  kind: "phase_change";
  to: string;
  reason: string;
}

interface QuickRepliesResult {
  kind: "quick_replies";
  options: string[];
}

type AnyPatch =
  | ProposeResult
  | ProposeUpdateResult
  | PhaseChangeResult
  | QuickRepliesResult;

/** Type-narrow a tool_call.result into a patch we recognise. Returns
 *  null for anything else so the caller can short-circuit cleanly. */
export function patchFromToolCall(
  toolName: string,
  result: unknown,
): AnyPatch | null {
  if (
    toolName === "propose_requirement" &&
    isObject(result) &&
    result.kind === "propose"
  ) {
    return result as unknown as ProposeResult;
  }
  if (
    toolName === "update_requirement" &&
    isObject(result) &&
    result.kind === "proposeUpdate"
  ) {
    return result as unknown as ProposeUpdateResult;
  }
  if (
    toolName === "advance_phase" &&
    isObject(result) &&
    result.kind === "phase_change"
  ) {
    return result as unknown as PhaseChangeResult;
  }
  if (
    toolName === "suggest_quick_replies" &&
    isObject(result) &&
    result.kind === "quick_replies" &&
    Array.isArray(result.options)
  ) {
    return result as unknown as QuickRepliesResult;
  }
  return null;
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

/* ── Card ─────────────────────────────────────────────────────────────── */

export function RequirementPatchCard({ patch }: { patch: AnyPatch }) {
  // `propose` patches render at the chat level via `ProposeBatchCard`
  // (a single one-line summary per turn). Skip here so callers that
  // still route propose patches through this component don't
  // double-render.
  if (patch.kind === "propose") return null;
  if (patch.kind === "phase_change") return <PhaseChangeCard patch={patch} />;
  // Quick-reply chips render via the dedicated `QuickReplyChips`
  // component (split out by ChatMessage). Render nothing as a safety.
  if (patch.kind === "quick_replies") return null;
  return <UpdateCard patch={patch} />;
}

const PHASE_LABEL: Record<string, string> = {
  mission: "Phase 1 · Mission",
  operational: "Phase 2 · Operational concept",
  stakeholder_needs: "Phase 3 · Stakeholder needs",
  system_ears: "Phase 4 · System requirements",
  verification: "Phase 5 · Verification & traceability",
};

function PhaseChangeCard({ patch }: { patch: PhaseChangeResult }) {
  const label = PHASE_LABEL[patch.to] ?? patch.to;
  return (
    <Box
      sx={(t) => ({
        mt: 1,
        borderRadius: 1.5,
        border: `1px solid ${
          t.palette.border?.default ?? t.palette.divider
        }`,
        background: alpha(t.palette.text.primary, 0.04),
        px: 1.5,
        py: 1,
      })}
    >
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <CheckIcon
          sx={(t) => ({ fontSize: 14, color: t.palette.primary.main })}
        />
        <Typography
          variant="overline"
          sx={(t) => ({
            fontSize: "0.625rem",
            fontWeight: 600,
            letterSpacing: "0.16em",
            color: t.palette.text.secondary,
            textTransform: "lowercase",
            lineHeight: 1.3,
          })}
        >
          moved to · {label.toLowerCase()}
        </Typography>
      </Stack>
      {patch.reason && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            fontSize: "0.72rem",
            color: "text.secondary",
            mt: 0.25,
            lineHeight: 1.4,
          }}
        >
          {patch.reason}
        </Typography>
      )}
    </Box>
  );
}

// ProposeCard removed — suggestions now live in the requirements list
// at status='suggested' (see ProposeBatchCard for the chat-side
// one-line summary and CategorySection for the list-side review).

function UpdateCard({ patch }: { patch: ProposeUpdateResult }) {
  const projectId = useProjectStore((s) => s.activeProject);
  const patchMutation = usePatchRequirement(projectId ?? "");
  const specQuery = useRequirementsSpec(projectId);

  // Detect already-applied: look up the live row and compare the
  // proposed field. If it matches `patch.after` (and `patch.before`
  // is the historical value), the patch has been applied. We can't
  // distinguish "applied via this card" from "applied independently"
  // — both render as accepted, which is the right UX (the field is in
  // the requested state either way).
  const live = useMemo(
    () =>
      specQuery.data?.requirements.find((r) => r.id === patch.requirementId),
    [specQuery.data?.requirements, patch.requirementId],
  );
  const liveValue = live ? readField(live, patch.field) : undefined;
  const alreadyApplied =
    live !== undefined &&
    liveValue !== undefined &&
    String(liveValue) === String(patch.after);

  const rejectionKey = useMemo(
    () =>
      patchRejectionKey({
        kind: "proposeUpdate",
        requirementId: patch.requirementId,
        field: patch.field,
        after: patch.after,
      }),
    [patch.requirementId, patch.field, patch.after],
  );
  const isRejected = useDecisionStore((s) => Boolean(s.rejected[rejectionKey]));
  const markRejected = useDecisionStore((s) => s.markRejected);

  const [localAccepted, setLocalAccepted] = useState(false);
  const autoAppliedRef = useRef(false);

  // Optimistic-with-undo: fire the patch on mount so the change lands
  // without a confirmation step. Skip if the value is already at
  // target (alreadyApplied) or the user previously dismissed it
  // (isRejected — covers refresh after Undo).
  useEffect(() => {
    if (autoAppliedRef.current) return;
    if (!projectId) return;
    if (alreadyApplied || isRejected) return;
    autoAppliedRef.current = true;
    patchMutation.mutate(
      {
        publicId: patch.requirementId,
        body: { [patch.field]: patch.after } as Record<string, unknown>,
      },
      {
        onSuccess: () => setLocalAccepted(true),
      },
    );
  }, [
    projectId,
    alreadyApplied,
    isRejected,
    patch.requirementId,
    patch.field,
    patch.after,
    patchMutation,
  ]);

  const decision: "pending" | "accepted" | "rejected" = alreadyApplied
    ? "accepted"
    : localAccepted
      ? "accepted"
      : isRejected
        ? "rejected"
        : "pending";

  const onApply = () => {
    if (!projectId) return;
    patchMutation.mutate(
      {
        publicId: patch.requirementId,
        body: { [patch.field]: patch.after } as Record<string, unknown>,
      },
      {
        onSuccess: () => setLocalAccepted(true),
      },
    );
  };
  const onUndo = () => {
    if (!projectId) return;
    patchMutation.mutate(
      {
        publicId: patch.requirementId,
        body: { [patch.field]: patch.before } as Record<string, unknown>,
      },
      {
        onSuccess: () => {
          // Persist as rejected so a refresh doesn't auto-re-apply
          // the patch on next mount. Visually flips to the
          // "reverted" dim state.
          markRejected(rejectionKey);
          setLocalAccepted(false);
        },
      },
    );
  };
  const onReject = () => markRejected(rejectionKey);

  const fieldLabel = formatField(patch.field);

  return (
    <CardShell
      eyebrow={`update · ${fieldLabel}`}
      decision={decision}
      requirementId={patch.requirementId}
    >
      {patch.before !== null && patch.before !== undefined && (
        <FieldDelta label="Before" value={String(patch.before)} dim />
      )}
      <FieldDelta label="After" value={String(patch.after)} />

      {decision === "accepted" && (
        <UndoRow
          onUndo={onUndo}
          loading={patchMutation.isPending}
          disabled={!projectId}
        />
      )}
      {decision === "pending" && (
        <ActionRow
          onAccept={onApply}
          onReject={onReject}
          loading={patchMutation.isPending}
          acceptLabel="Apply change"
          disabled={!projectId}
        />
      )}
    </CardShell>
  );
}

/* ── Sub-components ───────────────────────────────────────────────────── */

function CardShell({
  eyebrow,
  decision,
  requirementId,
  children,
}: {
  eyebrow: string;
  decision: "pending" | "accepted" | "rejected";
  requirementId: string | null;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={(t) => ({
        mt: 1,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor:
          decision === "accepted"
            ? alpha(t.palette.primary.main, 0.5)
            : decision === "rejected"
              ? t.palette.border?.default ?? t.palette.divider
              : alpha(t.palette.editorial?.main ?? "#d4a05c", 0.4),
        background:
          decision === "rejected"
            ? "transparent"
            : t.palette.surface?.raised ?? t.palette.background.paper,
        overflow: "hidden",
        opacity: decision === "rejected" ? 0.55 : 1,
      })}
    >
      <Box
        sx={(t) => ({
          px: 1.5,
          py: 0.75,
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          borderBottom: `1px solid ${
            t.palette.border?.default ?? t.palette.divider
          }`,
          background:
            decision === "accepted"
              ? alpha(t.palette.primary.main, 0.06)
              : alpha(t.palette.editorial?.main ?? "#d4a05c", 0.06),
        })}
      >
        <Box
          sx={(t) => ({
            width: 6,
            height: 6,
            borderRadius: "50%",
            background:
              decision === "accepted"
                ? t.palette.primary.main
                : t.palette.editorial?.main ?? "#d4a05c",
          })}
        />
        <Typography
          variant="overline"
          sx={(t) => ({
            fontSize: "0.625rem",
            fontWeight: 600,
            letterSpacing: "0.16em",
            textTransform: "lowercase",
            color:
              decision === "accepted"
                ? t.palette.primary.main
                : t.palette.editorial?.contrastText ?? "#f0c794",
            lineHeight: 1.3,
          })}
        >
          {eyebrow}
        </Typography>
        {requirementId && <RequirementId id={requirementId} />}
        {decision === "accepted" && (
          <Typography
            variant="caption"
            sx={{
              ml: "auto",
              color: "primary.main",
              fontSize: "0.7rem",
              fontWeight: 600,
            }}
          >
            <CheckIcon sx={{ fontSize: 12, verticalAlign: "-1px", mr: 0.25 }} />
            Saved
          </Typography>
        )}
        {decision === "rejected" && (
          <Typography
            variant="caption"
            sx={{
              ml: "auto",
              color: "text.disabled",
              fontSize: "0.7rem",
              fontWeight: 600,
            }}
          >
            <UndoIcon sx={{ fontSize: 12, verticalAlign: "-1px", mr: 0.25 }} />
            Reverted
          </Typography>
        )}
      </Box>
      <Box sx={{ px: 1.5, py: 1.25 }}>{children}</Box>
    </Box>
  );
}

function UndoRow({
  onUndo,
  loading,
  disabled,
}: {
  onUndo: () => void;
  loading: boolean;
  disabled: boolean;
}) {
  return (
    <Stack direction="row" sx={{ mt: 1.5 }}>
      <Button
        size="small"
        variant="text"
        color="inherit"
        onClick={onUndo}
        disabled={loading || disabled}
        startIcon={
          loading ? (
            <CircularProgress size={12} thickness={4} />
          ) : (
            <UndoIcon sx={{ fontSize: 14 }} />
          )
        }
        sx={(t) => ({
          minHeight: 28,
          py: 0.25,
          fontSize: "0.75rem",
          color: t.palette.text.secondary,
          "&:hover": { color: t.palette.text.primary },
        })}
      >
        {loading ? "Undoing…" : "Undo"}
      </Button>
    </Stack>
  );
}

function ActionRow({
  onAccept,
  onReject,
  loading,
  acceptLabel,
  disabled,
}: {
  onAccept: () => void;
  onReject: () => void;
  loading: boolean;
  acceptLabel: string;
  disabled: boolean;
}) {
  return (
    <Stack direction="row" spacing={0.75} sx={{ mt: 1.5 }}>
      <Button
        size="small"
        variant="contained"
        color="primary"
        onClick={onAccept}
        disabled={loading || disabled}
        startIcon={
          loading ? (
            <CircularProgress size={12} thickness={4} />
          ) : (
            <CheckIcon sx={{ fontSize: 14 }} />
          )
        }
        sx={{ minHeight: 28, py: 0.25, fontSize: "0.75rem" }}
      >
        {loading ? "Saving…" : acceptLabel}
      </Button>
      <Button
        size="small"
        variant="text"
        startIcon={<EditIcon sx={{ fontSize: 13 }} />}
        sx={{ minHeight: 28, py: 0.25, fontSize: "0.75rem" }}
        disabled={loading}
        onClick={() => {
          // "Edit" is a soft hint — for v1 it just rejects so the user
          // edits via the inline editor on the page. A future iteration
          // can open a sheet that pre-fills the editor with the proposal.
          onReject();
        }}
      >
        Edit
      </Button>
      <Button
        size="small"
        variant="text"
        color="inherit"
        sx={{
          minHeight: 28,
          py: 0.25,
          fontSize: "0.75rem",
          color: "text.disabled",
        }}
        disabled={loading}
        onClick={onReject}
      >
        Reject
      </Button>
    </Stack>
  );
}

function FieldDelta({
  label,
  value,
  dim = false,
}: {
  label: string;
  value: string;
  dim?: boolean;
}) {
  return (
    <Box
      sx={(t) => ({
        display: "grid",
        gridTemplateColumns: "48px 1fr",
        alignItems: "baseline",
        gap: 1,
        py: 0.4,
        opacity: dim ? 0.7 : 1,
        "& + &": {
          borderTop: `1px dashed ${
            t.palette.border?.default ?? t.palette.divider
          }`,
        },
      })}
    >
      <Typography
        variant="caption"
        sx={{
          fontSize: "0.65rem",
          color: "text.disabled",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          fontWeight: 600,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.8125rem",
          color: dim ? "text.secondary" : "text.primary",
          textDecoration: dim ? "line-through" : "none",
          textDecorationColor: "var(--mui-palette-text-disabled, gray)",
          lineHeight: 1.45,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function formatField(field: string): string {
  // Snake → space, lowercased.
  return field.replace(/_/g, " ");
}

/* ── Persistence helpers ─────────────────────────────────────────────── */

function readField(req: Requirement, field: string): unknown {
  // Map a field name to the live value on the row. Mirrors the
  // backend's PATCH allow-list — anything outside that set returns
  // undefined and the "already applied" check falls through to the
  // pending state.
  switch (field) {
    case "statement":
      return req.statement;
    case "priority":
      return req.priority;
    case "status":
      return req.status;
    case "rationale":
      return req.rationale;
    case "acceptance":
      return req.acceptance;
    case "verification":
      return req.verification;
    case "source":
      return req.source;
    case "scope":
      return req.scope;
    default:
      return undefined;
  }
}
